import React from 'react';
import { motion } from 'framer-motion';
import { GlassCard } from '@/components/ui/glass-card';
import Layout from '@/components/Layout';
import { useLanguage } from '@/context/LanguageContext';
import { BookOpen, Target, Heart, Star, TrendingUp, Users } from 'lucide-react';

export default function AboutPage() {
  const { language } = useLanguage();

  const schoolHistory = {
    en: `Established in 2002, our school began its inspiring journey with a vision to provide quality education and holistic development to young learners. Starting with just 13 students, the institution was founded on strong values, dedication, and a commitment to excellence in education. Over the years, through consistent efforts, innovative teaching practices, and the trust of parents and the community, the school has grown remarkably and proudly educates more than 1100 students as of 2026.\n\nFrom its humble beginnings to becoming a well-recognized educational institution, our school has continuously evolved to meet the changing needs of modern education. We emphasize academic excellence while equally focusing on character building, leadership skills, creativity, and overall personality development.`,
    hi: `वर्ष 2002 में स्थापित हमारा विद्यालय गुणवत्तापूर्ण शिक्षा एवं समग्र विकास की मजबूत सोच के साथ अपनी प्रेरणादायक यात्रा की शुरुआत करता है। मात्र 13 विद्यार्थियों से प्रारंभ हुआ यह संस्थान आज निरंतर प्रयास, समर्पण और अभिभावकों के विश्वास के कारण वर्ष 2026 तक 1100 से अधिक विद्यार्थियों का शिक्षण केंद्र बन चुका है।\n\nअपने प्रारंभिक चरण से लेकर आज तक विद्यालय ने आधुनिक शिक्षा की आवश्यकताओं के अनुरूप निरंतर प्रगति और विकास किया है। हमारा उद्देश्य केवल शैक्षणिक उत्कृष्टता तक सीमित नहीं है, बल्कि विद्यार्थियों के व्यक्तित्व विकास, नैतिक मूल्यों, नेतृत्व क्षमता और आत्मविश्वास को भी समान महत्व देना है।`
  };

  const timeline = [
    { year: '2002', event: 'School Founded', students: 13, milestone: 'Started with 13 students' },
    { year: '2005', event: 'First Batch Success', students: 50, milestone: '100% Board Results' },
    { year: '2010', event: 'Infrastructure Expansion', students: 250, milestone: 'New Building Inaugurated' },
    { year: '2015', event: 'Smart Classrooms', students: 600, milestone: 'Technology Integration' },
    { year: '2020', event: 'Excellence Recognition', students: 900, milestone: 'Award for Best School' },
    { year: '2026', event: 'Continued Growth', students: 1100, milestone: '96% Board Result Achievement' }
  ];

  const values = [
    { icon: BookOpen, title: 'Academic Excellence', description: 'Commitment to high-quality education and continuous learning' },
    { icon: Heart, title: 'Strong Values', description: 'Building character through moral and ethical education' },
    { icon: Users, title: 'Holistic Development', description: 'Focus on overall personality and skill development' },
    { icon: Target, title: 'Innovation', description: 'Modern teaching methods and technology integration' },
    { icon: Star, title: 'Discipline', description: 'Creating a structured and safe learning environment' },
    { icon: TrendingUp, title: 'Growth Mindset', description: 'Encouraging students to reach their full potential' }
  ];

  return (
    <Layout>
      {/* Hero Section */}
      <section className="py-20 bg-gradient-to-r from-blue-900 via-blue-800 to-blue-950 text-white" data-testid="about-hero">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1 className="text-5xl md:text-7xl font-bold mb-6">
              {language === 'en' ? 'About Our School' : 'हमारे स्कूल के बारे में'}
            </h1>
            <p className="text-xl md:text-2xl text-blue-100 max-w-3xl mx-auto">
              {language === 'en'
                ? 'A journey of excellence, dedication, and transformative education since 2002'
                : '2002 से उत्कृष्टता, समर्पण और परिवर्तनकारी शिक्षा की यात्रा'
              }
            </p>
          </motion.div>
        </div>
      </section>

      {/* School History */}
      <section className="py-20 bg-white" data-testid="school-history">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl md:text-5xl font-bold text-blue-900 mb-8 text-center">
              {language === 'en' ? 'School History' : 'विद्यालय का इतिहास'}
            </h2>
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <div>
                <div className="prose prose-lg max-w-none">
                  {schoolHistory[language].split('\n\n').map((para, index) => (
                    <p key={index} className="text-slate-600 leading-relaxed mb-6">
                      {para}
                    </p>
                  ))}
                </div>
              </div>
              <div>
                <GlassCard className="p-8">
                  <img
                    src="images/Branchimages/b_58.jpeg"
                    alt="School Building"
                    className="rounded-2xl shadow-xl w-full h-96 object-cover"
                  />
                </GlassCard>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Timeline */}
      <section className="py-20 bg-slate-50" data-testid="timeline-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl md:text-5xl font-bold text-blue-900 mb-12 text-center">
            {language === 'en' ? 'Our Growth Journey' : 'हमारी विकास यात्रा'}
          </h2>

          <div className="relative">
            {/* Timeline Line */}
            <div className="hidden md:block absolute left-1/2 transform -translate-x-1/2 h-full w-1 bg-blue-200" />

            <div className="space-y-12">
              {timeline.map((item, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: index % 2 === 0 ? -50 : 50 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className={`flex items-center ${index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'}`}
                >
                  <div className={`w-full md:w-5/12 ${index % 2 === 0 ? 'md:text-right' : 'md:text-left'}`}>
                    <GlassCard className="p-6">
                      <div className="text-4xl font-bold text-blue-900 mb-2">{item.year}</div>
                      <h3 className="text-xl font-bold text-slate-800 mb-2">{item.event}</h3>
                      <p className="text-slate-600 mb-2">{item.milestone}</p>
                      <div className="inline-block px-3 py-1 bg-amber-100 text-amber-700 text-sm font-semibold rounded-full">
                        {item.students} Students
                      </div>
                    </GlassCard>
                  </div>

                  <div className="hidden md:flex w-2/12 justify-center">
                    <div className="w-6 h-6 bg-blue-900 rounded-full ring-4 ring-blue-200 z-10" />
                  </div>

                  <div className="hidden md:block w-5/12" />
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Vision & Mission */}
      <section className="py-20 bg-gradient-to-br from-blue-900 to-blue-950 text-white" data-testid="vision-mission">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <div className="glass-dark rounded-3xl p-8">
                <div className="w-16 h-16 bg-amber-500 rounded-2xl flex items-center justify-center mb-6">
                  <Target className="w-8 h-8 text-white" />
                </div>
                <h2 className="text-3xl font-bold mb-4">
                  {language === 'en' ? 'Our Vision' : 'हमारी दृष्टि'}
                </h2>
                <p className="text-blue-100 leading-relaxed text-lg">
                  To create a positive and inspiring learning environment that promotes academic excellence and strong values. We aim to develop confident, responsible, and future-ready students through modern education and holistic growth.
                </p>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              viewport={{ once: true }}
            >
              <div className="glass-dark rounded-3xl p-8">
                <div className="w-16 h-16 bg-amber-500 rounded-2xl flex items-center justify-center mb-6">
                  <BookOpen className="w-8 h-8 text-white" />
                </div>
                <h2 className="text-3xl font-bold mb-4">
                  {language === 'en' ? 'Our Mission' : 'हमारा मिशन'}
                </h2>
                <p className="text-blue-100 leading-relaxed text-lg">
                  Our mission is to provide quality education that nurtures academic excellence, strong values, and overall personality development. We believe that every child has unique potential, and our goal is to provide the right guidance, opportunities, and environment to help them grow.
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Our Values */}
      <section className="py-20 bg-white" data-testid="our-values">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl md:text-5xl font-bold text-blue-900 mb-4 text-center">
            {language === 'en' ? 'Our Core Values' : 'हमारे मूल मूल्य'}
          </h2>
          <p className="text-lg text-slate-600 text-center mb-12 max-w-2xl mx-auto">
            {language === 'en'
              ? 'The principles that guide our educational philosophy and practices'
              : 'हमारी शैक्षिक दर्शन और व्यवहार को मार्गदर्शन करने वाले सिद्धांत'
            }
          </p>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {values.map((value, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <GlassCard className="h-full hover:shadow-2xl">
                  <div className="w-14 h-14 bg-gradient-to-br from-blue-600 to-blue-900 rounded-xl flex items-center justify-center mb-4">
                    <value.icon className="w-7 h-7 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-blue-900 mb-2">{value.title}</h3>
                  <p className="text-slate-600">{value.description}</p>
                </GlassCard>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </Layout>
  );
}